import React, {useRef} from "react";
import {Dropdown} from "primereact/dropdown";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {Calendar} from "primereact/calendar";
import {classNames} from "primereact/utils";
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {setUserField} from "../../../../redux/actions/actionsUsersList";
import {
    USER_WEEKDAYS,
    UserWorkScheduler,
    dateToSchedulerTime,
    defaultUserWorkScheduler,
    normalizeUserWorkSchedulers,
    schedulerTimeToDate,
} from "../userWorkSchedulers";

const SCHEDULE_TYPES = [
    {name: 'Дні тижня', typeScheduler: 402},
];

const UserWorkSchedule = () => {
    const dispatch = useAppDispatch();
    const userData = useAppSelector(state => state.UsersListReducer.userData);
    const errorFields = useAppSelector(state => state.UsersListReducer.errorFields);
    const usersActiveTableToggle = useAppSelector(state => state.UsersListReducer.usersActiveTableToggle);
    const usersDialogUnzipping = useAppSelector(state => state.UsersListReducer.usersDialogUnzipping);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const schedulers = normalizeUserWorkSchedulers(userData?.user?.workSchedulers);
    const schedulersRef = useRef<UserWorkScheduler[]>(schedulers);
    schedulersRef.current = schedulers;
    const previousTimeValues = useRef<Record<number, {startTime?: string | null; endTime?: string | null}>>({});
    const canEdit = usersDialogUnzipping || (usersActiveTableToggle && editType);
    const hasScheduleError = !!errorFields?.workSchedulers;

    const replaceSchedulers = (next: UserWorkScheduler[]) => {
        schedulersRef.current = next;
        dispatch(setUserField({section: 'user', fieldName: 'workSchedulers', value: next}));
    };

    const updateDayTime = (dayOfWeek: number, type: 'startTime' | 'endTime', time: string | null) => {
        const next = (schedulersRef.current || []).map((day) => (
            day.dayOfWeek === dayOfWeek ? {...day, [type]: time} : day
        ));
        replaceSchedulers(next);
    };

    const toggleDay = (dayOfWeek: number, checked: boolean) => {
        const current = schedulersRef.current || [];
        if (checked) {
            if (current.some((day) => day.dayOfWeek === dayOfWeek)) {
                return;
            }
            replaceSchedulers([...current, defaultUserWorkScheduler(dayOfWeek)]);
            return;
        }
        replaceSchedulers(current.filter((day) => day.dayOfWeek !== dayOfWeek));
    };

    const handleTimeFocus = (dayOfWeek: number, type: 'startTime' | 'endTime') => {
        const day = schedulersRef.current.find((item) => item.dayOfWeek === dayOfWeek);
        if (!day?.[type]) {
            return;
        }
        previousTimeValues.current[dayOfWeek] = {
            ...previousTimeValues.current[dayOfWeek],
            [type]: day[type],
        };
        updateDayTime(dayOfWeek, type, null);
    };

    const handleTimeBlur = (dayOfWeek: number, type: 'startTime' | 'endTime') => {
        const day = schedulersRef.current.find((item) => item.dayOfWeek === dayOfWeek);
        if (!day || day[type]) {
            return;
        }
        const previous = previousTimeValues.current[dayOfWeek]?.[type];
        const fallback = defaultUserWorkScheduler(dayOfWeek)[type];
        updateDayTime(dayOfWeek, type, previous || fallback);
    };

    const commitTypedTime = (dayOfWeek: number, type: 'startTime' | 'endTime', rawValue: string) => {
        const digits = rawValue.replace(/\D/g, '').slice(0, 4);
        if (digits.length !== 4) {
            return;
        }
        const hours = parseInt(digits.slice(0, 2), 10);
        const minutes = parseInt(digits.slice(2, 4), 10);
        if (hours > 23 || minutes > 59) {
            return;
        }
        const current = schedulerTimeToDate(schedulersRef.current.find((day) => day.dayOfWeek === dayOfWeek)?.[type]) || new Date();
        current.setHours(hours, minutes, 0, 0);
        updateDayTime(dayOfWeek, type, dateToSchedulerTime(current));
    };

    const maskTimeInput = (event: React.FormEvent<HTMLInputElement>, dayOfWeek: number, type: 'startTime' | 'endTime') => {
        const input = event.target as HTMLInputElement;
        const digits = input.value.replace(/\D/g, '').slice(0, 4);
        const hours = digits.slice(0, 2);
        const minutes = digits.slice(2, 4);
        const display = hours + (digits.length > 2 ? `:${minutes}` : '');
        if (input.value !== display) {
            const caret = digits.length + (digits.length > 2 ? 1 : 0);
            input.value = display;
            input.setSelectionRange(caret, caret);
        }
        commitTypedTime(dayOfWeek, type, digits);
    };

    return (
        <div className="grid p-fluid">
            <div className="field col-12 md:col-6 mb-0">
                <Dropdown
                    filter
                    value={402}
                    options={SCHEDULE_TYPES}
                    optionLabel="name"
                    optionValue="typeScheduler"
                    placeholder="Виберіть тип:"
                    className="w-full py-2"
                    disabled={!canEdit}
                />
            </div>
            <div className="field col-12 md:col-6"></div>
            <div className="field col-12 md:col-6">
                <label className={classNames('mb-3 font-bold', {'p-error': hasScheduleError})}>Розклад дзвінків</label>
                {USER_WEEKDAYS.map((field) => {
                    const active = schedulers.some((day) => day.dayOfWeek === field.dayOfWeek);
                    const day = schedulers.find((item) => item.dayOfWeek === field.dayOfWeek);
                    return (
                        <div className="field col-12 md:col-12 mb-0 p-0" key={field.dayOfWeek}>
                            <div className="grid align-items-center">
                                <div className="col-12 md:col-4">
                                    <InputText type="text" value={field.label} className="opacity-100 w-full" placeholder={field.label} disabled/>
                                </div>
                                <div className="col-2 md:col-2 flex justify-content-center">
                                    <Checkbox
                                        disabled={!canEdit}
                                        className={classNames('opacity-100', {'p-invalid': hasScheduleError && !schedulers.length})}
                                        checked={active}
                                        onChange={(event) => toggleDay(field.dayOfWeek, !!event.checked)}
                                    />
                                </div>
                                <div className="col-6 md:col-3 flex align-items-center">
                                    <div className="mr-2">
                                        <label>З</label>
                                    </div>
                                    <Calendar
                                        key={`start-user-${field.dayOfWeek}-${active}`}
                                        className={classNames('opacity-100', {'p-invalid': hasScheduleError && active})}
                                        disabled={!canEdit || !active}
                                        value={schedulerTimeToDate(day?.startTime)}
                                        onChange={(event) => {
                                            if (event.value instanceof Date && !isNaN(event.value.getTime())) {
                                                updateDayTime(field.dayOfWeek, 'startTime', dateToSchedulerTime(event.value));
                                            }
                                        }}
                                        placeholder="00:00"
                                        timeOnly
                                        hourFormat="24"
                                        onFocus={() => handleTimeFocus(field.dayOfWeek, 'startTime')}
                                        onBlur={() => handleTimeBlur(field.dayOfWeek, 'startTime')}
                                        onInput={(event) => maskTimeInput(event, field.dayOfWeek, 'startTime')}
                                    />
                                </div>
                                <div className="col-6 md:col-3 flex align-items-center">
                                    <div className="mr-2">
                                        <label>По</label>
                                    </div>
                                    <Calendar
                                        key={`end-user-${field.dayOfWeek}-${active}`}
                                        className={classNames('opacity-100', {'p-invalid': hasScheduleError && active})}
                                        disabled={!canEdit || !active}
                                        value={schedulerTimeToDate(day?.endTime)}
                                        onChange={(event) => {
                                            if (event.value instanceof Date && !isNaN(event.value.getTime())) {
                                                updateDayTime(field.dayOfWeek, 'endTime', dateToSchedulerTime(event.value));
                                            }
                                        }}
                                        placeholder="00:00"
                                        timeOnly
                                        hourFormat="24"
                                        onFocus={() => handleTimeFocus(field.dayOfWeek, 'endTime')}
                                        onBlur={() => handleTimeBlur(field.dayOfWeek, 'endTime')}
                                        onInput={(event) => maskTimeInput(event, field.dayOfWeek, 'endTime')}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="grid col-12 md:col-12 pt-0">
                {hasScheduleError && (
                    <div className="col-12 md:col-12 p-0 m-0 pl-2 mb-2">
                        <span className="title p-error text-sm">{errorFields?.workSchedulers?.workSchedulers}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserWorkSchedule;
